// @ts-strict-ignore
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnInit,
  Output,
  QueryList,
  ViewChild,
  ViewChildren
} from '@angular/core';
import { LMListCardService } from '@list/services/lm-list-card.service';
import { LMUrlUtilService } from '@services/utils';
import { LMComponentInfoService } from '@services/lm-componentinfo.service';
import {
  SohoContextMenuDirective,
  SohoPopupMenuComponent,
  SohoRatingComponent
} from 'ids-enterprise-ng';
import {LMLargeImageIsDialogService, LMListSpecUtilService, LMNavigationService} from '@app/components';
import { LMCardBaseDirective } from '@card/lm-card-base.directive';
import { isEmpty } from 'lodash-es';
import { LMContextService } from '@app/services';

@Component({
  selector: 'lm-list-card',
  standalone: false,
  templateUrl: './lm-list-card.component.html',
  styleUrls: [ './lm-list-card.component.scss' ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class LMListCardComponent extends LMCardBaseDirective implements OnInit, AfterViewInit {

  @ViewChild(SohoContextMenuDirective, { static: false }) contextMenu: SohoContextMenuDirective;
  @ViewChild(SohoPopupMenuComponent) popupMenuComponent: SohoPopupMenuComponent;
  @ViewChild('FlexContainer') flexContainer: ElementRef;
  @ViewChildren('CardColumn') cardColumns: QueryList<ElementRef>;
  @ViewChildren(SohoRatingComponent) ratings;
  @HostListener('window:resize') onResize() {
    // LMWEB-2465
    // Timeout to allow DOM reflow to finish before check
    setTimeout(() => this.checkRightColumn(), 250);
  }

  @HostListener('window:click', [ '$event' ])
  onWindowClick(event: MouseEvent): void {
    if (this.buttonMenuOpen !== undefined) {
      this.buttonMenuOpen = false;
    }
  }

  @Input() listUrlInfo: LMListUrlInfo;
  @Input() listSpec: LMListSpec;
  @Input() set dataView(incDataView: LMDataView) {
    this._dataView = incDataView;

    if (this.isContextCardView) {
      this.initCardTemplateData();
    }
  }

  get dataView(): LMDataView {
    return this._dataView;
  }

  @Input() rowIndex: number;
  @Input() boldFirstRow = true;
  @Input() isContextCardView = false;

  @Output() uiLinkEvent = new EventEmitter<LMUILinkEvent>();
  @Output() buildButtonMenu = new EventEmitter<Array<LMCardViewButton>>();
  @Output() drillAroundEvent = new EventEmitter<LMCardListDrillEvent>();

  public isFilterList: boolean;
  public isSelectList: boolean;
  public isCardViewWithDetailSections: boolean;
  public cardTemplateData: LMCardTemplateData;
  public noDataAvailable: string;
  public automationId: string;
  public isSmartPhone: boolean;
  public lastButtonMenuElement: HTMLElement;
  public flyoutTooltip: string;

  private buttonMenuOpen = false;
  private _dataView: LMDataView;

  constructor(
    protected lmComponentInfoService: LMComponentInfoService,
    protected lmNavigationService: LMNavigationService,
    protected lmUrlUtilService: LMUrlUtilService,
    private lmListCardService: LMListCardService,
    private changeDetectorRef: ChangeDetectorRef,
    private lmLargeImageIsDialogService: LMLargeImageIsDialogService,
    private lmContextService: LMContextService,
    private lmListSpecUtilService:LMListSpecUtilService
  ) {
    super(lmComponentInfoService, lmNavigationService, lmUrlUtilService);
  }

  ngOnInit() {
    this.isFilterList = this.listSpec.listType === 'filterlist';
    this.isSelectList = this.listSpec.listType === 'select';
    this.isCardViewWithDetailSections = this.lmListSpecUtilService.isCardViewWithDetailSections(this.listSpec);
    this.initCardTemplateData();
    this.noDataAvailable = Soho.Locale.translate('NoDataAvailable');
    this.automationId = this.cardTemplateData.automationId;
    this.isSmartPhone = this.lmContextService.isSmartPhone();
    this.flyoutTooltip = Soho.Locale.translate('FlyoutOpenFullScreen');
  }

  ngAfterViewInit() {
    this.checkRightColumn();

    setTimeout(() => {
      this.changeDetectorRef.markForCheck();

      if (this.ratings) {
        // ensure all rating components are disabled
        this.ratings.forEach((rating: SohoRatingComponent) => {
          rating.readonly();
        });
      }

      if (this?.cardTemplateData?.buttonMenu) {
        this.buildButtonMenu.emit(this.cardTemplateData.buttonMenu);
      }
    }, 1);
  }

  public initCardTemplateData() {
    let displayFields: LMListDisplayField = this.listSpec.busListSpec.displayFields[0];
    let automationId = this.listSpec.busListSpec.automationId;

    if (this.isContextCardView) {
      displayFields = this.listSpec.busListSpec.contextCardView;
      automationId = this.listSpec.busListSpec.automationId + '-context';
    } else if (this.listSpec.busListSpec.responsiveCardView && this.lmContextService.isSmartPhone()) {
      displayFields = this.listSpec.busListSpec.responsiveCardView;
    }
    const targetDataView = this.listSpec?.isTemplateList ? this.dataView.dataView : this.dataView;
    this.cardTemplateData = this.lmListCardService.getCardTemplateData(
      displayFields,
      targetDataView,
      automationId,
      this?.listSpec?.busListSpec?.openActionRestricted,
      this?.isFilterList,
      this?.isCardViewWithDetailSections,
      this?.isSelectList
    );
  }

  public navigate(event: Event, item: LMListViewTemplateField, navigationParams?: LMCardNavigationParams) {
    // For keydown events, only proceed on Enter key
    if (event instanceof KeyboardEvent) {
      if (event.key !== 'Enter') {
        return;
      }
    }

    // prevent row from activating when clicking on the image
    event.stopImmediatePropagation();

    if (item === null || item === undefined && navigationParams) {
      const ctrlKeyPressed = (event as KeyboardEvent | MouseEvent).ctrlKey;
      const openInOverlay = ctrlKeyPressed ? false : navigationParams.openInOverlay;
      const columnDef = openInOverlay ? { displayField: { openInOverlay: true, navigationTargetComponent: navigationParams.navigationTargetComponent } } : null;
      this.uiLinkEvent.emit({
        columnDef,
        dataView: navigationParams.dataView,
        event,
        navigationParams
      });
    } else {
      const ctrlKeyPressed = (event as KeyboardEvent | MouseEvent).ctrlKey;
      const openInOverlay = ctrlKeyPressed ? false : item.openInOverlay;
      const columnDef = openInOverlay ? { displayField: { openInOverlay: true, navigationTargetComponent: item.navigationTargetComponent } } : null;
      this.uiLinkEvent.emit({
        columnDef,
        dataView: item.navigationParams.dataView,
        event,
        navigationParams: item.navigationParams
      });
    }
  }

  public openLink(event: Event, href: string, isExternal?: boolean) {
    // For keydown events, only proceed on Enter key
    if (event instanceof KeyboardEvent) {
      if (event.key !== 'Enter') {
        return;
      }
    }

    // prevent row from activating when clicking on the image
    event.stopImmediatePropagation();

    if (typeof href === 'string' && (href.startsWith('http') || href.startsWith('https'))) {
      window.open(href, isExternal ? '_blank' : '_self');
    }
  }

  public openLargeImage(event: Event, largeImage: LMLargeImageIsInfo) {
    // For keydown events, only proceed on Enter key
    if (event instanceof KeyboardEvent) {
      if (event.key !== 'Enter') {
        return;
      }
    }

    // prevent row from activating when clicking on the image
    event.stopImmediatePropagation();

    this.lmLargeImageIsDialogService.openLargeImageDialog(largeImage);
  }

  public displayFieldAsTitle(index: number, columns: LMCardTemplateColumn): boolean {

    if (columns.column === undefined) {
      return false;
    }

    return index === 0 && columns.column.length > 1;
  }

  public alertClass(item: LMListViewTemplateField): string {
    return 'icon-' + item.alertCondition.icon;
  }

  private checkRightColumn(): void {
    let mainColumnHeight = 0;

    if (this.cardColumns === undefined || this.cardColumns.length === 0) {
      return;
    }

    if (this.cardColumns.length === 2) {
      this.cardColumns?.forEach((elementRef, index) => {
        if (index === 0) {
          mainColumnHeight = elementRef?.nativeElement?.clientHeight;
        }

        if (index === 1) {
          if (this?.flexContainer?.nativeElement?.clientHeight > mainColumnHeight) {
            elementRef.nativeElement.classList.add('lm-card-column-wrapped');
          } else {
            elementRef?.nativeElement?.classList.remove('lm-card-column-wrapped');
          }
        }
      });
    }
  }

  public getComponentInfo(additionalInfo?: LMCompInfoAdditionalInfo): LMComponentInfo {
    let cardviewSpec: LMCardviewSpec = {};
    if (this.isContextCardView) {
      cardviewSpec = this.listSpec.busListSpec.contextCardView;
    } else if (this.listSpec.busListSpec.displayFields && this.listSpec.busListSpec.displayFields[0].type === 'CardView') {
      cardviewSpec = this.listSpec.busListSpec.displayFields[0];
    }

    if (!isEmpty(cardviewSpec)) {
      const compConfigInfo: ConfigInfo = {};
      compConfigInfo['personalization'] = cardviewSpec ? cardviewSpec.personalization : undefined;
      compConfigInfo['configuration'] = cardviewSpec ? cardviewSpec.configuration : undefined;
      compConfigInfo['configStamp'] = cardviewSpec ? cardviewSpec.configStamp : 0;
      compConfigInfo['classConfigStamp'] = cardviewSpec ? cardviewSpec.classConfigStamp : 0;

      if (cardviewSpec.type === 'CardView') {
        cardviewSpec['componentType'] =  'CARDVIEW';
      }
      cardviewSpec['configInfo'] = compConfigInfo;

      return {
        componentType: cardviewSpec.componentType,
        name: cardviewSpec ? cardviewSpec.id : undefined,
        cardviewSpec,
        hasConfigAccess: false,
        type: 'card',
      } as LMComponentInfo;
    }
  }

  public onButtonMenu(event) {
    // For keydown events, only proceed on Enter key
    if (event instanceof KeyboardEvent) {
      if (event.key !== 'Enter') {
        return;
      }
    }

    this.lastButtonMenuElement = event.target as HTMLElement;

    if (this.buttonMenuOpen) {
      this.contextMenu.close();
      this.buttonMenuOpen = false;
    } else if (this.contextMenu && !this.buttonMenuOpen) {
      this.contextMenu.initializeComponent();
      this.buttonMenuOpen = true;
    }

    // prevent row from activating when clicking on the button menu
    event.stopImmediatePropagation();
  }

  onDrillClicked(event: MouseEvent) {
    const rowIndexString = (event.target as Element).getAttribute('row-index');
    const rowIndex = parseInt(rowIndexString, 10);
    this.drillAroundEvent.emit({ rowIndex });
  }
}
